/**
 * Metrics Display Component - RESPONSIVE REIMAGINED
 * Production metrics with YTD & Adjusted Targets - works perfectly on all screen sizes
 */

import React from 'react';
import './MetricsDisplay.css';
import { format } from 'date-fns';

const MetricsDisplay = ({ entitiesWithYTD }) => {
  const metrics = entitiesWithYTD.filter(e => 
    e.EntityType === 'Metric' && 
    e.ytdMetrics &&
    (e.Category === 'Production' || !e.Category)  
  );
  
  if (metrics.length === 0) {
    return (
      <div className="metrics-display-empty">
        <p>No metrics data available yet.</p>
        <p>Add metrics and upload daily production values to see YTD analysis.</p>
      </div>
    );
  }

  const getTrendArrow = (trend) => {
    if (trend === 'up') return '↑';
    if (trend === 'down') return '↓';
    return '→';
  };

  const formatNumber = (num, decimals = 0) => {
    if (num == null) return '0';
    const formatted = num.toFixed(decimals);
    return formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  return (
    <div className="metrics-display">
      <div className="metrics-header">
        <h3>📊 Production Metrics - YTD Performance</h3>
      </div>

      <div className="metrics-grid">
        {metrics.map(metric => {
          const ytd = metric.ytdMetrics;
          const hasAdjustedTarget = metric.hasAdjustedTarget;
          const adjustedTarget = metric.adjustedTargetValue || metric.TargetValue;
          const baseTarget = metric.baseTargetValue || metric.TargetValue;
          const upside = adjustedTarget - baseTarget;
          
          // Calculate variance against adjusted target
          const varianceFromAdjusted = ytd.ytdAverage - adjustedTarget;
          const variancePercentAdjusted = adjustedTarget !== 0 ? (varianceFromAdjusted / adjustedTarget) * 100 : 0;
          
          const statusColor = ytd.statusColor || 'gray';
          const isPositive = varianceFromAdjusted >= 0;

          return (
            <div key={metric.Id} className={`metric-card border-${statusColor}`}>
              
              {/* Header Row */}
              <div className="card-header">
                <h4 className="metric-name">{metric.Title}</h4>
                <span className={`badge badge-${statusColor}`}>
                  {ytd.status}
                </span>
              </div>

              {/* Main Stats Row */}
              <div className="stats-row">
                {/* YTD Average - Primary */}
                <div className="stat-primary">
                  <div className="stat-label">YTD Average</div>
                  <div className="stat-value-lg">
                    <span className="number">{formatNumber(ytd.ytdAverage, 1)}</span>
                    <span className="unit">{ytd.unit}</span>
                    <span className={`trend trend-${ytd.trend}`}>
                      {getTrendArrow(ytd.trend)}
                    </span>
                  </div>
                </div>

                {/* Target - Secondary (now shows adjusted if available) */}
                <div className="stat-secondary">
                  <div className="stat-label">
                    {hasAdjustedTarget ? 'Adjusted Target' : 'Target'}
                  </div>
                  <div className="stat-value-md">
                    <span className="number">{formatNumber(adjustedTarget, 1)}</span>
                    <span className="unit">{ytd.unit}</span>
                  </div>
                </div>
              </div>

              {/* Target Breakdown - NEW (only if adjusted) */}
              {hasAdjustedTarget && (
                <div className="target-breakdown">
                  <div className="target-flow">
                    <div className="target-item base">
                      <span className="target-item-label">Base</span>
                      <span className="target-item-value">{formatNumber(baseTarget, 0)}</span>
                    </div>
                    <div className="target-plus">+</div>
                    <div className="target-item upside">
                      <span className="target-item-label">Upside</span>
                      <span className="target-item-value">{formatNumber(upside, 0)}</span>
                    </div>
                    <div className="target-equals">=</div>
                    <div className="target-item adjusted">
                      <span className="target-item-label">Adjusted</span>
                      <span className="target-item-value">{formatNumber(adjustedTarget, 0)}</span>
                    </div>
                  </div>
                  
                  {/* Contributions List */}
                  {metric.targetContributions && metric.targetContributions.length > 0 && (
                    <div className="contributions-list">
                      {metric.targetContributions.map((contrib, idx) => (
                        <div key={idx} className="contribution-chip">
                          <span className="chip-icon">✓</span>
                          <span className="chip-text">
                            +{formatNumber(contrib.amount, 0)} from <strong>{contrib.source}</strong>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Variance Bar */}
              <div className="variance-section">
                <div className="variance-bar-bg">
                  <div 
                    className={`variance-bar-fill ${isPositive ? 'positive' : 'negative'}`}
                    style={{ width: `${Math.min(Math.abs(variancePercentAdjusted), 100)}%` }}
                  />
                </div>
                <div className="variance-text">
                  <span className={`variance-num ${isPositive ? 'positive' : 'negative'}`}>
                    {isPositive ? '+' : ''}{formatNumber(varianceFromAdjusted, 1)} {ytd.unit}
                  </span>
                  <span className="variance-pct">
                    ({isPositive ? '+' : ''}{formatNumber(variancePercentAdjusted, 1)}%)
                  </span>
                </div>
              </div>

              {/* Footer Stats */}
              <div className="footer-stats">
                <div className="footer-stat">
                  <span className="footer-label">Latest</span>
                  <span className="footer-value">
                    {formatNumber(ytd.latestValue, 1)} {ytd.unit}
                    <span className={`trend-sm trend-${ytd.recentTrend}`}>
                      {getTrendArrow(ytd.recentTrend)}
                    </span>
                  </span>
                </div>
                <div className="footer-stat">
                  <span className="footer-label">Data Points</span>
                  <span className="footer-value">{ytd.dataPoints}</span>
                </div>
              </div>

              <div className="data-timestamp">
                <span className="timestamp-icon">🕒</span>
                <span className="timestamp-text">
                  Latest data: {ytd.endDate ? format(new Date(ytd.endDate), 'MMM dd, yyyy HH:mm') : 'N/A'}
                </span>
              </div>

              {/* Description */}
              {metric.Description && (
                <div className="card-description">
                  {metric.Description}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MetricsDisplay;